import { ArticleStatus, StockStatus } from "@/services/product/product.config";
import { CustomProduct } from "@apptus/esales-api";
import { Market } from "@features/market/config/i18n-config";

export type ProductStockStatus = {
  code: StockStatus;
  inStock: boolean;
  stockOnHand: number;
  purchasable: boolean;
  oversellable: boolean;
  deliveryDate?: string;
  deliveryTime?: string;
};

const deliveryDateFormatters = new Map<string, Intl.DateTimeFormat>();

function getFormatter(locale: string) {
  let formatter = deliveryDateFormatters.get(locale);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    deliveryDateFormatters.set(locale, formatter);
  }
  return formatter;
}

function getDeliveryTime(locale: string, days?: number) {
  if (days === undefined) {
    return undefined;
  }
  const date = new Date();
  date.setUTCDate(date.getUTCDate() + days);
  return getFormatter(locale).format(date);
}

function getDeliveryDate(locale: string, nextDeliveryDate?: string) {
  if (!nextDeliveryDate) {
    return undefined;
  }
  const date = new Date(nextDeliveryDate);
  const now = new Date();
  const deliveryDay = Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
  );
  const today = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
  );

  if (Number.isNaN(deliveryDay) || deliveryDay <= today) {
    return getDeliveryTime(locale, 1);
  }

  return getFormatter(locale).format(date);
}

export default function getProductStockStatus(
  product: CustomProduct,
  market: Market,
): ProductStockStatus {
  const stockOnHand =
    product.typedCustom?.numbers.stockOnHand ??
    product.variants[0]?.stockNumber ??
    0;
  const articleStatus = product.custom?.itemStatus?.[0]?.id;
  const stockStatus = product.custom?.stockStatus?.[0]?.id;
  const nextDeliveryDate = product.custom?.nextDeliveryDate?.[0]?.id;
  const deliveryTimeDays = product.typedCustom?.numbers.delivery_time_days ?? 0;

  const deliveryTime = getDeliveryTime(market.locale, deliveryTimeDays);
  const deliveryDate = getDeliveryDate(market.locale, nextDeliveryDate);

  /** Edge case for when included items are out of stock */
  if (stockStatus === "OutOfStock") {
    if (deliveryDate) {
      return {
        code: StockStatus.Incoming,
        inStock: false,
        stockOnHand: 0,
        purchasable: false,
        oversellable: articleStatus === ArticleStatus.backorder_item,
        deliveryDate,
      };
    }
    return {
      code: StockStatus.OutOfStock,
      inStock: false,
      stockOnHand: 0,
      purchasable: false,
      oversellable: false,
      deliveryDate,
    };
  }

  switch (articleStatus) {
    case ArticleStatus.backorder_item: {
      if (stockOnHand > 0) {
        return {
          code: StockStatus.StockItem,
          inStock: true,
          stockOnHand: stockOnHand >= 10 ? Infinity : stockOnHand,
          purchasable: true,
          oversellable: true,
          deliveryTime,
        };
      }
      if (deliveryDate) {
        return {
          code: StockStatus.Incoming,
          inStock: false,
          stockOnHand: 0,
          purchasable: true,
          oversellable: true,
          deliveryDate,
        };
      }
      return {
        code: StockStatus.DeliveryItem,
        inStock: false,
        stockOnHand: 0,
        purchasable: true,
        oversellable: true,
        deliveryTime,
      };
    }
    case ArticleStatus.stock_item: {
      if (stockOnHand > 0) {
        return {
          code: StockStatus.PendingOutOfStock,
          inStock: true,
          stockOnHand: stockOnHand >= 10 ? Infinity : stockOnHand,
          purchasable: true,
          oversellable: false,
          deliveryTime,
        };
      }
      if (deliveryDate) {
        return {
          code: StockStatus.Incoming,
          inStock: false,
          stockOnHand: 0,
          purchasable: false,
          oversellable: false,
          deliveryDate,
        };
      }
      return {
        code: StockStatus.OutOfStock,
        inStock: false,
        stockOnHand: 0,
        purchasable: false,
        oversellable: false,
        deliveryDate,
      };
    }
    case ArticleStatus.out_of_stock_at_supplier:
    case ArticleStatus.discontinued_item:
    case ArticleStatus.terminated_collaboration_sell:
    case ArticleStatus.limited_discontinue: {
      if (stockOnHand > 0) {
        return {
          code: StockStatus.PendingSoldOut,
          inStock: true,
          stockOnHand: stockOnHand >= 10 ? Infinity : stockOnHand,
          purchasable: true,
          oversellable: false,
          deliveryTime,
        };
      }
      if (deliveryDate) {
        return {
          code: StockStatus.Incoming,
          inStock: false,
          stockOnHand: 0,
          purchasable: false,
          oversellable: false,
          deliveryDate,
        };
      }
      return {
        code: StockStatus.Hidden,
        inStock: false,
        stockOnHand: 0,
        purchasable: false,
        oversellable: false,
      };
    }
    case ArticleStatus.limited_keep: {
      if (stockOnHand > 0) {
        return {
          code: StockStatus.PendingSoldOut,
          inStock: true,
          stockOnHand: stockOnHand >= 10 ? Infinity : stockOnHand,
          purchasable: true,
          oversellable: false,
        };
      }
      if (deliveryDate) {
        return {
          code: StockStatus.Incoming,
          inStock: false,
          stockOnHand: 0,
          purchasable: false,
          oversellable: false,
          deliveryDate,
        };
      }
      return {
        code: StockStatus.SoldOut,
        inStock: false,
        stockOnHand: 0,
        purchasable: false,
        oversellable: false,
      };
    }
    case ArticleStatus.sales_stop: {
      return {
        code: StockStatus.Hidden,
        inStock: false,
        stockOnHand: 0,
        purchasable: false,
        oversellable: false,
      };
    }
    case ArticleStatus.time_limited_keep: {
      if (stockOnHand > 0) {
        return {
          code: StockStatus.PendingOutOfStock,
          inStock: true,
          stockOnHand: stockOnHand >= 10 ? Infinity : stockOnHand,
          purchasable: true,
          oversellable: false,
          deliveryTime,
        };
      }
      if (deliveryDate) {
        return {
          code: StockStatus.Incoming,
          inStock: false,
          stockOnHand: 0,
          purchasable: false,
          oversellable: false,
          deliveryDate,
        };
      }
      return {
        code: StockStatus.OutOfStock,
        inStock: false,
        stockOnHand: 0,
        purchasable: false,
        oversellable: false,
        deliveryTime,
      };
    }
    case ArticleStatus.time_limited_discontinue:
    case ArticleStatus.limited_discontinue: {
      if (stockOnHand > 0) {
        return {
          code: StockStatus.PendingSoldOut,
          inStock: true,
          stockOnHand: stockOnHand >= 10 ? Infinity : stockOnHand,
          purchasable: true,
          oversellable: false,
          deliveryTime,
        };
      }
      return {
        code: StockStatus.Hidden,
        inStock: false,
        stockOnHand: 0,
        purchasable: false,
        oversellable: false,
      };
    }
    case ArticleStatus.unconfirmed: {
      if (stockOnHand > 0) {
        return {
          code: StockStatus.StockItem,
          inStock: true,
          stockOnHand: stockOnHand >= 10 ? Infinity : stockOnHand,
          purchasable: true,
          oversellable: false,
          deliveryTime,
        };
      }
      if (deliveryDate) {
        return {
          code: StockStatus.Incoming,
          inStock: false,
          stockOnHand: 0,
          purchasable: false,
          oversellable: false,
          deliveryDate,
        };
      }
      return {
        code: StockStatus.DeliveryItem,
        inStock: false,
        stockOnHand: 0,
        purchasable: true,
        oversellable: false,
        deliveryTime,
      };
    }
    default: {
      if (deliveryDate) {
        return {
          code: StockStatus.Incoming,
          inStock: false,
          stockOnHand: 0,
          purchasable: false,
          oversellable: false,
          deliveryDate,
        };
      }
      return {
        code: StockStatus.Unknown,
        inStock: false,
        stockOnHand: 0,
        purchasable: false,
        oversellable: false,
        deliveryTime,
      };
    }
  }
}
